/**
 * Shared label maps + option lists for the Security Vendor Directory UI (VIS-199).
 * Enum values mirror the Prisma enums in vendor-service.
 */
import type {
  SecurityDomain,
  ServiceRegion,
  ComplianceCertification,
  SecurityVendorTier,
  SecurityVendorStatus,
  VendorContractStatus,
  VendorSortField,
} from '@/services/securityVendor.service';

export const DOMAIN_LABELS: Record<SecurityDomain, string> = {
  PHYSICAL_SECURITY: 'Physical Security',
  CYBERSECURITY: 'Cybersecurity',
  EXECUTIVE_PROTECTION: 'Executive Protection',
  SURVEILLANCE: 'Surveillance',
  ACCESS_CONTROL: 'Access Control',
  INVESTIGATIONS: 'Investigations',
  THREAT_INTELLIGENCE: 'Threat Intelligence',
  GUARD_SERVICES: 'Guard Services',
  SECURITY_CONSULTING: 'Security Consulting',
  PENETRATION_TESTING: 'Penetration Testing',
  SIEM_SOC: 'SIEM / SOC',
  INCIDENT_RESPONSE: 'Incident Response',
};

export const REGION_LABELS: Record<ServiceRegion, string> = {
  NORTH_AMERICA: 'North America',
  EMEA: 'EMEA',
  APAC: 'APAC',
  LATAM: 'LATAM',
};

export const CERTIFICATION_LABELS: Record<ComplianceCertification, string> = {
  SOC2: 'SOC 2',
  ISO_27001: 'ISO 27001',
  HITRUST: 'HITRUST',
  FEDRAMP: 'FedRAMP',
  PCI_DSS: 'PCI DSS',
  GDPR: 'GDPR',
  HIPAA: 'HIPAA',
  NIST_CSF: 'NIST CSF',
  CMMC: 'CMMC',
};

export const TIER_LABELS: Record<SecurityVendorTier, string> = {
  ENTERPRISE: 'Enterprise',
  MID_MARKET: 'Mid-Market',
  SMB: 'SMB',
};

export const STATUS_LABELS: Record<SecurityVendorStatus, string> = {
  NOMINATED: 'Nominated',
  VETTING: 'Vetting',
  TRIAL: 'Trial',
  APPROVED: 'Approved',
  RECOMMENDED: 'Recommended',
  ON_PROBATION: 'On Probation',
  DEPRECATED: 'Deprecated',
  BLACKLISTED: 'Blacklisted',
};

export const CONTRACT_STATUS_LABELS: Record<VendorContractStatus, string> = {
  ACTIVE: 'Active',
  EXPIRED: 'Expired',
  UNDER_REVIEW: 'Under Review',
  NEW: 'New',
};

/** Tailwind classes for a status pill. */
export const STATUS_PILL: Record<SecurityVendorStatus, string> = {
  NOMINATED: 'bg-secondary-100 text-secondary-700 dark:bg-secondary-700 dark:text-secondary-200',
  VETTING: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  TRIAL: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  APPROVED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  RECOMMENDED: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  ON_PROBATION: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  DEPRECATED: 'bg-secondary-200 text-secondary-600 dark:bg-secondary-800 dark:text-secondary-400',
  BLACKLISTED: 'bg-danger-100 text-danger-800 dark:bg-danger-900/40 dark:text-danger-300',
};

/**
 * Lifecycle transition graph (VIS-203) — MUST mirror the backend FSM in
 * `libs/types` (SECURITY_VENDOR_STATUS_TRANSITIONS). The UI only offers legal next
 * states so it never triggers a backend 400 (L-032). Guarded by a unit test.
 */
export const STATUS_TRANSITIONS: Record<SecurityVendorStatus, SecurityVendorStatus[]> = {
  NOMINATED: ['VETTING', 'BLACKLISTED'],
  VETTING: ['TRIAL', 'APPROVED', 'DEPRECATED', 'BLACKLISTED'],
  TRIAL: ['APPROVED', 'DEPRECATED', 'BLACKLISTED'],
  APPROVED: ['RECOMMENDED', 'ON_PROBATION', 'DEPRECATED', 'BLACKLISTED'],
  RECOMMENDED: ['ON_PROBATION', 'DEPRECATED', 'BLACKLISTED'],
  ON_PROBATION: ['APPROVED', 'RECOMMENDED', 'DEPRECATED', 'BLACKLISTED'],
  DEPRECATED: ['VETTING', 'BLACKLISTED'],
  BLACKLISTED: [],
};

/** Legal next statuses from `status`. */
export function nextStatuses(status: SecurityVendorStatus): SecurityVendorStatus[] {
  return STATUS_TRANSITIONS[status] ?? [];
}

/**
 * A vendor currently carries the "Verified Recommended" badge only when it is
 * BOTH still RECOMMENDED and has a verify stamp. No transition clears
 * `verifiedRecommendedAt`, so gating the badge on the stamp alone left it showing
 * after a RECOMMENDED→BLACKLISTED/DEPRECATED move (UI drift — L-032/L-040/L-041).
 */
export function isVerifiedRecommended(vendor: {
  status: SecurityVendorStatus;
  verifiedRecommendedAt?: string | null;
}): boolean {
  return vendor.status === 'RECOMMENDED' && !!vendor.verifiedRecommendedAt;
}

/** True when the vendor's current status can legally transition to RECOMMENDED (verify-eligible). */
export function canVerifyRecommended(status: SecurityVendorStatus): boolean {
  return nextStatuses(status).includes('RECOMMENDED');
}

/** Review cadence labels (VIS-203). */
export const REVIEW_CADENCE_LABELS: Record<'QUARTERLY' | 'SEMI_ANNUAL' | 'ANNUAL', string> = {
  QUARTERLY: 'Quarterly',
  SEMI_ANNUAL: 'Semi-Annual',
  ANNUAL: 'Annual',
};

export const DOMAIN_VALUES = Object.keys(DOMAIN_LABELS) as SecurityDomain[];
export const REGION_VALUES = Object.keys(REGION_LABELS) as ServiceRegion[];
export const CERTIFICATION_VALUES = Object.keys(CERTIFICATION_LABELS) as ComplianceCertification[];
export const TIER_VALUES = Object.keys(TIER_LABELS) as SecurityVendorTier[];
export const STATUS_VALUES = Object.keys(STATUS_LABELS) as SecurityVendorStatus[];
export const CONTRACT_STATUS_VALUES = Object.keys(CONTRACT_STATUS_LABELS) as VendorContractStatus[];

/**
 * Directory sort options (VIS-201). Each pairs a sort field with an order; "region"
 * is intentionally absent (multi-value array — no sensible SQL sort).
 */
export const SORT_OPTIONS: ReadonlyArray<{
  value: string;
  label: string;
  sortBy: VendorSortField;
  sortOrder: 'asc' | 'desc';
}> = [
  { value: 'updatedAt:desc', label: 'Recently updated', sortBy: 'updatedAt', sortOrder: 'desc' },
  { value: 'compositeRating:desc', label: 'Highest rating', sortBy: 'compositeRating', sortOrder: 'desc' },
  { value: 'lastReviewedAt:desc', label: 'Most recently reviewed', sortBy: 'lastReviewedAt', sortOrder: 'desc' },
  { value: 'name:asc', label: 'Alphabetical (A–Z)', sortBy: 'name', sortOrder: 'asc' },
  { value: 'createdAt:desc', label: 'Newest', sortBy: 'createdAt', sortOrder: 'desc' },
];
